/**
 * Unified exports for exercise drag & drop components.
 * Automatically selects the correct implementation based on platform:
 * - react-dnd for desktop/web (non-mobile)
 * - @dnd-kit for mobile devices (iOS and Android)
 *
 * Platform detection is centralized here for easy modification in the future.
 * To change which library is used, modify the `USE_DND_KIT_FOR_ALL_PLATFORMS` constant below.
 */

import React, { memo } from "react";
import { useDevice } from "../../../_layout";
import {
  DraggableExercise as DraggableExerciseDndKit,
  EmptyWorkoutDropZone as EmptyWorkoutDropZoneDndKit,
  TrailingDropZone as TrailingDropZoneDndKit,
  ExerciseDndProvider as ExerciseDndProviderDndKit,
  ExerciseDropZone as ExerciseDropZoneDndKit,
} from "./dndKit";
import {
  DraggableExerciseReactDnd,
  EmptyWorkoutDropZoneReactDnd,
  TrailingDropZoneReactDnd,
  ExerciseDndProviderReactDnd,
} from "./reactDnd";

// ============================================================================
// Platform Detection Configuration
// ============================================================================

/**
 * Platform detection mode configuration.
 *
 * Set to `true` to always use @dnd-kit for all platforms (ignores isMobile detection).
 * Set to `false` to use @dnd-kit only for mobile devices (uses isMobile detection).
 *
 * Examples:
 * - Always use @dnd-kit: `USE_DND_KIT_FOR_ALL_PLATFORMS = true`
 * - Use @dnd-kit only for mobile: `USE_DND_KIT_FOR_ALL_PLATFORMS = false`
 */
const USE_DND_KIT_FOR_ALL_PLATFORMS = false;

/**
 * Determines which drag & drop library to use based on configuration and device type.
 *
 * @param {boolean} isMobile - Whether the current device is mobile (from useDevice hook)
 * @returns {boolean} - true to use @dnd-kit, false to use react-dnd
 */
const shouldUseDndKit = (isMobile) => {
  // Temporarily force react-dnd for all platforms (including mobile)
  return false;

  // Original logic (commented out for easy restoration):
  // if (USE_DND_KIT_FOR_ALL_PLATFORMS) {
  //   return true; // Always use @dnd-kit regardless of platform
  // }
  // return isMobile; // Use @dnd-kit only for mobile devices
};

// ============================================================================
// Unified Component Exports
// ============================================================================

/**
 * Unified DraggableExercise component
 * Automatically selects @dnd-kit or react-dnd based on platform configuration
 */
export const DraggableExercise = memo((props) => {
  const { isMobile } = useDevice();
  return shouldUseDndKit(isMobile) ? (
    <DraggableExerciseDndKit {...props} />
  ) : (
    <DraggableExerciseReactDnd {...props} />
  );
});

/**
 * Unified EmptyWorkoutDropZone component
 * Automatically selects @dnd-kit or react-dnd based on platform configuration
 */
export const EmptyWorkoutDropZone = memo((props) => {
  const { isMobile } = useDevice();
  return shouldUseDndKit(isMobile) ? (
    <EmptyWorkoutDropZoneDndKit {...props} />
  ) : (
    <EmptyWorkoutDropZoneReactDnd {...props} />
  );
});

/**
 * Unified TrailingDropZone component
 * Automatically selects @dnd-kit or react-dnd based on platform configuration
 */
export const TrailingDropZone = memo((props) => {
  const { isMobile } = useDevice();
  return shouldUseDndKit(isMobile) ? (
    <TrailingDropZoneDndKit {...props} />
  ) : (
    <TrailingDropZoneReactDnd {...props} />
  );
});

/**
 * Unified ExerciseDndProvider component
 * Automatically selects @dnd-kit or react-dnd based on platform configuration
 */
export const ExerciseDndProvider = memo((props) => {
  const { isMobile } = useDevice();
  return shouldUseDndKit(isMobile) ? (
    <ExerciseDndProviderDndKit {...props} />
  ) : (
    <ExerciseDndProviderReactDnd {...props} />
  );
});

export const ExerciseDropZone = ExerciseDropZoneDndKit;
